/**
 * Desktop Mega Menu image preview (header-menu / aria-expanded themes)
 * Requirements:
 * - Links with: data-mega-menu-image="..."
 * - Preview container inside each submenu:
 *   <div data-mega-menu-preview hidden><img data-mega-menu-preview-img></div>
 *
 * Safe:
 * - No <details>, no toggle event
 * - Does NOT set --submenu-height (prevents "exploded" menu)
 * - Only updates the preview image
 */
(function () {
  const LINK = "[data-mega-menu-image]";
  const PREVIEW = "[data-mega-menu-preview]";
  const PREVIEW_IMG = "[data-mega-menu-preview-img]";
  const DEFAULT_IMAGE_ATTR = "data-mega-menu-default-image";

  const isDesktop = () =>
    window.matchMedia && window.matchMedia("(min-width: 990px)").matches;

  function getSubmenuInnerFromTrigger(triggerLink) {
    const controls = triggerLink?.getAttribute?.("aria-controls");
    if (!controls) return null;
    const el = document.getElementById(controls);
    if (!el) return null;
    return el.closest(".menu-list__submenu-inner") || el;
  }

  function getScopeFromAnyLink(a) {
    const byTrigger = getSubmenuInnerFromTrigger(a);
    if (byTrigger) return byTrigger;

    return (
      a.closest?.(".menu-list__submenu-inner") ||
      a.closest?.(".mega-menu__grid") ||
      null
    );
  }

  function getParts(scope) {
    if (!scope) return { preview: null, img: null };
    return {
      preview: scope.querySelector(PREVIEW),
      img: scope.querySelector(PREVIEW_IMG),
    };
  }

  function firstImageInPanel(scope) {
    const first = scope?.querySelector?.(LINK);
    return first ? first.getAttribute("data-mega-menu-image") : null;
  }

  function defaultImageInPanel(scope) {
    const { preview, img } = getParts(scope);
    return (
      preview?.getAttribute?.(DEFAULT_IMAGE_ATTR) ||
      img?.getAttribute?.("src") ||
      null
    );
  }

  function resetToDefault(scope) {
    const src = defaultImageInPanel(scope);
    if (src) show(scope, src);
    else hide(scope);
  }

  function show(scope, src) {
    const { preview, img } = getParts(scope);
    if (!preview || !img || !src) return;

    if (img.getAttribute("src") !== src) img.setAttribute("src", src);

    const unhide = () => {
      preview.hidden = false;
      window.dispatchEvent(new Event("resize"));
    };

    if (img.complete) {
      unhide();
    } else {
      img.addEventListener("load", unhide, { once: true });
      img.addEventListener(
        "error",
        () => {
          preview.hidden = true;
          window.dispatchEvent(new Event("resize"));
        },
        { once: true }
      );
    }
  }

  function hide(scope) {
    const { preview } = getParts(scope);
    if (preview) preview.hidden = true;
    window.dispatchEvent(new Event("resize"));
  }

  function setInitialForTrigger(triggerLink) {
    const scope = getSubmenuInnerFromTrigger(triggerLink);
    if (!scope) return;

    // Always start/reset from default image (if any)
    resetToDefault(scope);
  }

  function ensureInitialWhenEnteringPanel(scope) {
    if (!scope) return;
    const { preview } = getParts(scope);
    if (!preview) return;

    if (preview.hidden === false) return;

    resetToDefault(scope);
  }

  function onHoverOrFocus(e) {
    if (!isDesktop()) return;

    const a = e.target?.closest?.(".mega-menu__link");
    if (!a) return;

    const scope = getScopeFromAnyLink(a);
    if (!scope) return;

    const src = a.getAttribute("data-mega-menu-image");
    if (src) show(scope, src);
    else resetToDefault(scope);
  }

  document.addEventListener("pointerover", onHoverOrFocus, true);
  document.addEventListener("focusin", onHoverOrFocus, true);

  document.addEventListener(
    "pointerenter",
    (e) => {
      if (!isDesktop()) return;
      const scope = e.target?.closest?.(".menu-list__submenu-inner");
      if (!scope) return;
      ensureInitialWhenEnteringPanel(scope);
    },
    true
  );

  document.addEventListener(
    "pointerleave",
    (e) => {
      if (!isDesktop()) return;
      const scope = e.target;
      if (!(scope instanceof HTMLElement)) return;
      if (!scope.matches?.(".menu-list__submenu-inner")) return;
      resetToDefault(scope);
    },
    true
  );

  const mo = new MutationObserver((mutations) => {
    if (!isDesktop()) return;

    for (const m of mutations) {
      if (m.type !== "attributes" || m.attributeName !== "aria-expanded") continue;

      const el = m.target;
      if (!(el instanceof HTMLElement)) continue;
      if (!el.matches?.(".menu-list__link[aria-controls]")) continue;

      const expanded = el.getAttribute("aria-expanded") === "true";
      if (expanded) {
        setInitialForTrigger(el);
      } else {
        const scope = getSubmenuInnerFromTrigger(el);
        if (scope) resetToDefault(scope);
      }
    }
  });

  mo.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-expanded"],
  });

  window.addEventListener("load", () => {
    if (!isDesktop()) return;
    document
      .querySelectorAll(".menu-list__link[aria-controls][aria-expanded='true']")
      .forEach((a) => setInitialForTrigger(a));
  });
})();
